import { useEffect, useRef } from "react";
import type {
  KeyStatus,
  Model,
  Preset,
  StageStatus,
  TenantSettings,
  Voice,
} from "../types";
import { PresetManager } from "./PresetManager";
import { SettingsForm } from "./SettingsCard";

/* The instance's configuration, in a panel that takes over the right of the
   screen rather than the 292px sidebar card it used to share with the live
   controls. Two different jobs were sharing one box: the lock, the volume and
   the character are things you reach for mid-stream, and this is a form you sit
   down with. Only the first needs to be one click away. */

export type DrawerTab = "settings" | "presets";

interface Props {
  open: boolean;
  /* Which half is showing. Held by the caller so a command ("Manage
     characters") can open the drawer on the right one. */
  tab: DrawerTab;
  onClose: () => void;
  settings: TenantSettings | null;
  models: Model[];
  voices: Voice[];
  presets: Preset[];
  stages: StageStatus[];
  onPresetsChanged: () => void;
  keyStatus: KeyStatus | null;
  onSaved: (settings: TenantSettings) => void;
  onKeySaved: (status: KeyStatus) => void;
}

export function SettingsDrawer({
  open,
  tab,
  onClose,
  voices,
  presets,
  stages,
  onPresetsChanged,
  ...rest
}: Props) {
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);

  /* `onClose` is a fresh closure on every render of the panel above, and that
     panel re-renders on every socket frame. Held in a ref, the effect below can
     depend on `open` alone — keyed to the prop it would otherwise re-run on
     every message that arrives, moving focus out of whatever field the admin
     was typing in. */
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (!open) return;
    // Send focus back where it came from on close, or a keyboard user lands at
    // the top of the document every time they close this.
    opener.current = document.activeElement;
    panel.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close.current();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      (opener.current as HTMLElement | null)?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="drawer-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
        tabIndex={-1}
        ref={panel}
      >
        <header className="drawer-head">
          <h2 id="drawer-title">
            {tab === "settings" ? "Instance settings" : "Characters"}
          </h2>
          <button className="ghost" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="drawer-body">
          {tab === "settings" ? (
            <SettingsForm {...rest} />
          ) : (
            <PresetManager
              presets={presets}
              voices={voices}
              models={rest.models}
              stages={stages}
              onChanged={onPresetsChanged}
            />
          )}
        </div>
      </div>
    </div>
  );
}
